// Pure helpers for the GrooveSell revenue projections card.
// Kept free of Vue so they can be unit tested directly.

export const PROJECTION_CATEGORIES = [
  { key: 'monthly', label: 'Monthly', color: '#2f3e75' },
  { key: 'quarterly', label: 'Quarterly', color: '#4f8fbf' },
  { key: 'annual', label: 'Annual', color: '#5fa383' },
  { key: 'payment_plan', label: 'Payment plans', color: '#d4a13f' },
  { key: 'other', label: 'Other', color: '#9aa3b5' },
];

export const PROJECTION_RANGES = [
  { days: 7, label: 'Next 7 days' },
  { days: 30, label: 'Next 30 days' },
  { days: 90, label: 'Next 90 days' },
];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

export function formatMoney(amount, currency, wholeUnits = false) {
  const value = toNumber(amount);
  const code = typeof currency === 'string' && currency ? currency.toUpperCase() : 'USD';
  const options = { style: 'currency', currency: code };
  if (wholeUnits) {
    options.minimumFractionDigits = 0;
    options.maximumFractionDigits = 0;
  }
  try {
    return new Intl.NumberFormat('en-US', options).format(value);
  } catch (e) {
    return `${code} ${value.toFixed(wholeUnits ? 0 : 2)}`;
  }
}

export function formatRate(rate) {
  return `${Math.round(toNumber(rate) * 100)}%`;
}

export function findWindow(data, days) {
  const windows = (data && Array.isArray(data.windows)) ? data.windows : [];
  return windows.find((item) => Number(item.days) === Number(days)) || null;
}

// One stacked series per category, in the fixed category order, skipping
// categories that are zero for every bar in the window.
export function buildProjectionSeries(windowData, metric) {
  const bars = (windowData && Array.isArray(windowData.bars)) ? windowData.bars : [];
  const series = [];
  const colors = [];
  PROJECTION_CATEGORIES.forEach((category) => {
    const values = bars.map((bar) => toNumber(bar && bar[metric] && bar[metric][category.key]));
    if (values.some((value) => value !== 0)) {
      series.push({ name: category.label, data: values });
      colors.push(category.color);
    }
  });
  return {
    categories: bars.map((bar) => (bar && bar.label) || (bar && bar.date) || ''),
    series,
    colors,
  };
}

export function isWindowEmpty(windowData) {
  if (!windowData) {
    return true;
  }
  const totals = windowData.totals || {};
  return toNumber(totals.scheduled) === 0 && toNumber(totals.expected) === 0;
}

export function topProducts(windowData, limit = 5) {
  const products = (windowData && Array.isArray(windowData.products)) ? windowData.products : [];
  return products.slice(0, limit);
}

// "Monthly 80% · Annual 62% (typical)" for categories with scheduled revenue.
export function rateSummary(rates, windowData) {
  const byCategory = (windowData && windowData.by_category) || {};
  const allRates = rates || {};
  return PROJECTION_CATEGORIES
    .filter((category) => toNumber(byCategory[category.key] && byCategory[category.key].scheduled) > 0)
    .filter((category) => allRates[category.key])
    .map((category) => {
      const rate = allRates[category.key];
      const typical = rate.source === 'default' ? ' (typical)' : '';
      return `${category.label} ${formatRate(rate.rate)}${typical}`;
    })
    .join(' · ');
}

export function otherCurrenciesSummary(otherCurrencies) {
  if (!Array.isArray(otherCurrencies) || !otherCurrencies.length) {
    return '';
  }
  return otherCurrencies
    .map((item) => `${String(item.currency || '').toUpperCase()} ${formatMoney(item.scheduled_90, item.currency)}`)
    .join(', ');
}
